import React, { useEffect, useRef, useState } from 'react'
import { mountThreeScene } from '@lib/threeScene'
import RewardsArchitecture from './RewardsArchitecture'

/**
 * The total rewards stack as a real WebGL object: four slabs — base pay,
 * benefits, variable pay, equity — rising from a dark plinth, the top one in
 * brand gold.
 *
 * Deliberate constraints, because a hero object is not worth breaking the page
 * over:
 *
 *   - three.js is loaded with a dynamic import, so it lands in its own chunk
 *     and never blocks first paint. Roughly 150KB gzipped that most visitors
 *     fetch after the hero text is already readable.
 *   - Nothing loads until the canvas scrolls into view.
 *   - No WebGL, reduced-motion preference, a small screen, or any failure at
 *     all falls back to the SVG version of the same diagram. The fallback is
 *     what renders first, so there is never an empty box.
 *   - Everything is disposed on unmount; a leaked WebGL context survives
 *     client-side navigation and eventually loses the whole canvas.
 */

// Slabs run bottom to top. Sizes taper so the stack reads as a pyramid.
const SLABS = [
  // Tones step up the stack so each layer separates from the one below and
  // from the page. The original bottom slabs were near-black on black and
  // read as a hole rather than a base.
  { w: 3.4, d: 3.4, h: 0.42, y: 0.0, color: 0x6e6e6e, metal: 0.55, rough: 0.45, emissive: 0x141414 },
  { w: 2.85, d: 2.85, h: 0.4, y: 0.62, color: 0x8a7a52, metal: 0.6, rough: 0.4, emissive: 0x151008 },
  { w: 2.3, d: 2.3, h: 0.38, y: 1.22, color: 0xc79f22, metal: 0.68, rough: 0.32, emissive: 0x1d1503 },
  { w: 1.7, d: 1.7, h: 0.36, y: 1.8, color: 0xffd118, metal: 0.74, rough: 0.24, emissive: 0x3a2e00 }
]

export default function RewardsStack3D({ className = '' }) {
  const hostRef = useRef(null)
  const [active, setActive] = useState(false)

  useEffect(() => {
    const host = hostRef.current
    if (!host) return

    return mountThreeScene(
      host,
      ({ THREE, scene, camera }) => {
        camera.position.set(5.6, 4.4, 6.2)
        camera.lookAt(0, 1.1, 0)

        scene.add(new THREE.AmbientLight(0xffffff, 0.5))

        // Warm key from the upper right is what makes the gold read as metal
        // rather than flat yellow.
        const key = new THREE.DirectionalLight(0xfff0c4, 2.5)
        key.position.set(5, 8, 4)
        key.castShadow = true
        key.shadow.mapSize.set(1024, 1024)
        key.shadow.camera.near = 1
        key.shadow.camera.far = 30
        key.shadow.camera.left = -8
        key.shadow.camera.right = 8
        key.shadow.camera.top = 8
        key.shadow.camera.bottom = -8
        key.shadow.bias = -0.0015
        scene.add(key)

        // Cool rim from behind separates the silhouette from the black page.
        const rim = new THREE.DirectionalLight(0xbfd8ff, 1.7)
        rim.position.set(-6, 3.5, -5)
        scene.add(rim)

        const bounce = new THREE.PointLight(0xffd118, 1.5, 22)
        bounce.position.set(-2.5, 1.2, 3.2)
        scene.add(bounce)

        const group = new THREE.Group()
        const created = []

        SLABS.forEach((s) => {
          const geo = new THREE.BoxGeometry(s.w, s.h, s.d)
          const mat = new THREE.MeshStandardMaterial({
            color: s.color,
            metalness: s.metal,
            roughness: s.rough,
            emissive: s.emissive
          })
          const mesh = new THREE.Mesh(geo, mat)
          mesh.position.y = s.y
          mesh.castShadow = true
          mesh.receiveShadow = true
          group.add(mesh)
          created.push({ geo, mat })

          // Thin gold fillet on each slab's top face — the lit edge the CSS
          // surfaces use, carried into the 3D object.
          const edgeGeo = new THREE.BoxGeometry(s.w * 1.005, 0.02, s.d * 1.005)
          const edgeMat = new THREE.MeshStandardMaterial({
            color: 0xffe858,
            metalness: 1,
            roughness: 0.12,
            emissive: 0x6b5610,
            emissiveIntensity: 0.55
          })
          const edge = new THREE.Mesh(edgeGeo, edgeMat)
          edge.position.y = s.y + s.h / 2
          group.add(edge)
          created.push({ geo: edgeGeo, mat: edgeMat })
        })

        // Catcher plane so the stack casts onto something.
        const floorGeo = new THREE.CircleGeometry(6.5, 64)
        const floorMat = new THREE.ShadowMaterial({ opacity: 0.5 })
        const floor = new THREE.Mesh(floorGeo, floorMat)
        floor.rotation.x = -Math.PI / 2
        floor.position.y = -0.22
        floor.receiveShadow = true
        scene.add(floor)
        created.push({ geo: floorGeo, mat: floorMat })

        scene.add(group)

        return {
          update: (t, px = 0, py = 0) => {
            group.rotation.y = t + px * 0.3
            group.rotation.x = Math.sin(t * 0.6) * 0.045 + py * 0.17
            group.position.y = Math.sin(t * 1.15) * 0.05
          },
          dispose: () => created.forEach(({ geo, mat }) => { geo.dispose(); mat.dispose() })
        }
      },
      { onReady: () => setActive(true) }
    )
  }, [])

  return (
    <div className={`relative ${className}`}>
      {/* Rendered first and always present, so the slot is never empty and
          anyone without WebGL still gets the diagram.

          Hidden outright once the canvas is live rather than faded out: the
          renderer is alpha-transparent, so a fallback still occupying the box
          would show straight through the 3D object. Hiding depends on a class,
          not on a transition finishing — transitions do not progress in a
          backgrounded tab, which would leave both visible at once. */}
      <div className={active ? 'hidden' : ''} aria-hidden={active ? 'true' : undefined}>
        <RewardsArchitecture className="w-full h-auto" />
      </div>

      <div
        ref={hostRef}
        className={`absolute inset-0 transition-opacity duration-700 ${active ? 'opacity-100' : 'opacity-0'}`}
        role="img"
        aria-label="Rotating three-dimensional diagram of the total rewards stack: base pay at the foundation, then benefits, variable pay, and equity at the top."
      />
    </div>
  )
}
